import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Home, Loader2, X, ChevronsLeft, Inbox, Send, FileText, FilePen, Folder, Settings2, Plus, Mail, Megaphone, CircleDot, CheckCircle2, Archive, Clock, CalendarClock, Trash2, Hourglass } from 'lucide-react';
import { useEmailNav, type EmailOrdner } from '../../pages/admin/emails/EmailNavContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { useSidebarCollapse } from '../../contexts/SidebarCollapseContext';
import { useSidebarDaten } from './useSidebarDaten';
import { IM_EMAIL_BEREICH } from '../../lib/immobilien/sidebarNav';
import {
  TOP_LEVEL_ITEMS, SIDEBAR_CATEGORIES, SELECTED_CATEGORY_STORAGE_KEY,
  type SidebarItem, type SidebarCategory,
} from './sidebarConfig';
import {
  isItemVisible, visibleCategories, findCategoryIdForItem,
  resolveSelectedCategory, categoryHasUnread, routeItemId, alsModulListe,
  type VisibilityContext,
} from '../../lib/sidebar/navigation';

interface SidebarProps {
  activePath: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  userEmail?: string;
  userRole?: string;
  avatarUrl?: string;
  fullName?: string;
  allowedModules?: any;
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activePath,
  onNavigate,
  onLogout,
  userEmail,
  userRole,
  avatarUrl,
  fullName,
  allowedModules,
  isOpen,
  onClose,
}) => {
  const isSuperAdmin = userRole?.toLowerCase() === 'super admin';
  const isAdmin = userRole?.toLowerCase() === 'admin';
  const isTeam = userRole?.toLowerCase() === 'team';

  const { activeModules, loading, rechnungenBadge, emailBadge, vorschlaegeBadge } = useSidebarDaten();
  const [dragOverObjektId, setDragOverObjektId] = useState<string | null>(null);
  const objektScrollRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef<{ dir: number; raf: number }>({ dir: 0, raf: 0 });
  const stoppeAutoScroll = () => {
    autoScrollRef.current.dir = 0;
    if (autoScrollRef.current.raf) { cancelAnimationFrame(autoScrollRef.current.raf); autoScrollRef.current.raf = 0; }
  };
  // Beim Ziehen am oberen/unteren Rand der Objektliste automatisch scrollen.
  const objektDragScroll = (e: React.DragEvent) => {
    const cont = objektScrollRef.current;
    if (!cont) return;
    const r = cont.getBoundingClientRect();
    const zone = 44;
    autoScrollRef.current.dir = e.clientY < r.top + zone ? -1 : e.clientY > r.bottom - zone ? 1 : 0;
    if (autoScrollRef.current.dir !== 0 && !autoScrollRef.current.raf) {
      const step = () => {
        const c = objektScrollRef.current;
        if (!c || autoScrollRef.current.dir === 0) { autoScrollRef.current.raf = 0; return; }
        c.scrollTop += autoScrollRef.current.dir * 10;
        autoScrollRef.current.raf = requestAnimationFrame(step);
      };
      autoScrollRef.current.raf = requestAnimationFrame(step);
    }
  };
  const { unreadByModul } = useNotification();
  const { isDemoMode } = useDemoMode();
  const { collapsed, toggle } = useSidebarCollapse();

  const getInitials = (name?: string, email?: string) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    if (email) return email.substring(0, 2).toUpperCase();
    return 'AD';
  };
  const initial = getInitials(fullName, userEmail);

  const getRoleLabel = () => {
    if (isSuperAdmin) return 'Super Admin';
    if (isAdmin) return 'Admin';
    if (isTeam) return 'Team';
    return userRole || 'User';
  };

  const currentId = routeItemId(activePath);

  // Kontextuelle E-Mail-Sub-Nav: im E-Mail-Bereich zeigt das Panel Postfach + Ordner statt Kategorie.
  const { nav: emailNav } = useEmailNav();
  const emailCategoryId = findCategoryIdForItem(SIDEBAR_CATEGORIES, 'emails');
  // Da "emails" in der Kategorie "Verwaltung" liegt, würde die E-Mail-Sub-Nav das Verwaltung-Panel
  // dauerhaft überdecken. Über dieses Flag beendet ein Kategorie-Klick die Sub-Nav; beim erneuten
  // Wechsel auf die E-Mail-Route (currentId-Effekt unten) wird sie wieder aktiviert.
  const [emailNavDismissed, setEmailNavDismissed] = useState(false);

  const visCtx: VisibilityContext = {
    isSuperAdmin,
    isTeam,
    activeModules,
    allowedModules: alsModulListe(allowedModules),
  };
  // Serienmail ist ein Ordner im E-Mail-Bereich (Entscheidung E-Mails 2) – kein eigener Menüpunkt mehr.
  const visibleCats = visibleCategories(SIDEBAR_CATEGORIES, visCtx)
    .map(c => ({ ...c, items: c.items.filter(i => !IM_EMAIL_BEREICH.has(i.id)) }))
    .filter(c => c.items.length > 0);
  const visibleTop = TOP_LEVEL_ITEMS.filter(item => isItemVisible(item, visCtx));

  const readStoredCategory = (): string | null => {
    try { return localStorage.getItem(SELECTED_CATEGORY_STORAGE_KEY); } catch { return null; }
  };
  const persistSelected = (id: string) => {
    try { localStorage.setItem(SELECTED_CATEGORY_STORAGE_KEY, id); } catch { /* ignore */ }
  };

  const [selectedCategory, setSelectedCategory] = useState<string>(() =>
    resolveSelectedCategory({
      routeItemId: currentId,
      stored: readStoredCategory(),
      categories: SIDEBAR_CATEGORIES,
      fallback: 'verwaltung',
    })
  );

  // Aktive Route → Panel-Kategorie synchronisieren (Top-Level lässt die letzte Kategorie stehen)
  useEffect(() => {
    // Route-Wechsel hebt ein evtl. gesetztes E-Mail-Sub-Nav-Dismiss wieder auf — so erscheint die
    // Sub-Nav beim (erneuten) Betreten des E-Mail-Bereichs, verschwindet aber, sobald man weg ist.
    setEmailNavDismissed(false);
    const routeCat = findCategoryIdForItem(SIDEBAR_CATEGORIES, currentId);
    if (routeCat) {
      setSelectedCategory(routeCat);
      persistSelected(routeCat);
    }
  }, [currentId]);

  // Auf sichtbare Kategorien klemmen (z. B. Team-User ohne die gespeicherte Kategorie)
  const effectiveSelected = visibleCats.some(c => c.id === selectedCategory)
    ? selectedCategory
    : visibleCats[0]?.id;
  const panelCategory = visibleCats.find(c => c.id === effectiveSelected) ?? null;

  // E-Mail-Sub-Nav zeigen, wenn wir auf der E-Mail-Route sind, im Panel die E-Mail-Kategorie gewählt
  // ist UND sie nicht per Kategorie-Klick beendet wurde. Sonst käme man aus ihr nicht mehr heraus.
  const emailMode = currentId === 'emails' && !!emailNav && effectiveSelected === emailCategoryId && !emailNavDismissed;

  const selectCategory = (id: string) => {
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
    // Wird gerade die E-Mail-Sub-Nav gezeigt, beendet ein Kategorie-Klick sie und zeigt die normale
    // Kategorie-Liste (NICHT einklappen) — sonst käme man aus der E-Mail-Sub-Nav nicht mehr heraus.
    if (emailMode) {
      setEmailNavDismissed(true);
      setSelectedCategory(id);
      persistSelected(id);
      if (collapsed) toggle();
      return;
    }
    // Klick auf die bereits offene Kategorie (Desktop) klappt das Panel ein
    if (isDesktop && !collapsed && id === effectiveSelected) {
      toggle();
      return;
    }
    setSelectedCategory(id);
    persistSelected(id);
    if (collapsed) toggle(); // eingeklappt → aufklappen
  };

  const unreadCtx = { rechnungenBadge, emailBadge, vorschlaegeBadge, unreadByModul };

  // Hover-Tooltip für Rail-Icons. position:fixed, damit es nicht vom overflow der
  // Kategorie-Nav abgeschnitten wird; Position aus der Bounding-Box des Icons.
  const [railTip, setRailTip] = useState<{ label: string; top: number; left: number } | null>(null);
  const showRailTip = (e: React.MouseEvent<HTMLElement>, label: string) => {
    const r = e.currentTarget.getBoundingClientRect();
    setRailTip({ label, top: r.top + r.height / 2, left: r.right + 10 });
  };
  const hideRailTip = () => setRailTip(null);

  const renderBadges = (id: string, variant: 'rail' | 'panel') => {
    const offset = variant === 'rail' ? '-right-2' : '-right-1.5';
    const leftOffset = variant === 'rail' ? '-left-2' : '-left-1.5';
    if (id === 'rechnungen' && rechnungenBadge > 0) {
      return (
        <span className={`absolute -top-1.5 ${offset} w-4 h-4 bg-red-500 text-white text-[7px] font-black rounded-full flex items-center justify-center shadow-sm`}>
          {rechnungenBadge > 9 ? '9+' : rechnungenBadge}
        </span>
      );
    }
    if (id === 'emails' && (emailBadge > 0 || vorschlaegeBadge > 0)) {
      return (
        <>
          {emailBadge > 0 && (
            <span className={`absolute -top-1.5 ${offset} w-4 h-4 bg-blue-500 text-white text-[7px] font-black rounded-full flex items-center justify-center shadow-sm`}>
              {emailBadge > 9 ? '9+' : emailBadge}
            </span>
          )}
          {vorschlaegeBadge > 0 && (
            <span className={`absolute -top-1.5 ${leftOffset} w-4 h-4 bg-violet-500 text-white text-[7px] font-black rounded-full flex items-center justify-center shadow-sm`}>
              {vorschlaegeBadge > 9 ? '9+' : vorschlaegeBadge}
            </span>
          )}
        </>
      );
    }
    if (!((id === 'rechnungen' && rechnungenBadge > 0) || (id === 'emails' && (emailBadge > 0 || vorschlaegeBadge > 0))) && unreadByModul[id] > 0) {
      return <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#c4a77d] rounded-full shadow-sm animate-pulse"></span>;
    }
    return null;
  };

  // ── Rail: Top-Level-Item (Dashboard) ──
  const renderRailTop = (item: SidebarItem) => {
    const Icon = item.icon;
    const isActive = currentId === item.id;
    return (
      <button
        key={item.id}
        onClick={() => onNavigate(item.path)}
        onMouseEnter={(e) => showRailTip(e, item.label)}
        onMouseLeave={hideRailTip}
        aria-label={item.label}
        className={`relative w-11 h-11 flex items-center justify-center rounded-[14px] transition-all shrink-0 ${
          isActive ? 'bg-[#0f172a] text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        <Icon size={20} strokeWidth={isActive ? 2.3 : 1.7} />
      </button>
    );
  };

  // ── Rail: Kategorie-Icon ──
  const renderRailCategory = (cat: SidebarCategory) => {
    const CatIcon = cat.icon;
    const isSelected = cat.id === effectiveSelected;
    const routeCatId = findCategoryIdForItem(SIDEBAR_CATEGORIES, currentId);
    const hasUnread = categoryHasUnread(cat, unreadCtx);
    return (
      <button
        key={cat.id}
        onClick={() => selectCategory(cat.id)}
        onMouseEnter={(e) => showRailTip(e, cat.label)}
        onMouseLeave={hideRailTip}
        aria-label={cat.label}
        className={`relative w-11 h-11 flex items-center justify-center rounded-[14px] transition-all shrink-0 ${
          isSelected
            ? 'bg-[#fdfbf8] text-[#c4a77d] shadow-[inset_0_0_0_1.5px_rgba(196,167,125,0.45)]'
            : 'text-gray-400 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        <CatIcon size={20} strokeWidth={isSelected ? 2.2 : 1.7} />
        {collapsed && hasUnread && (
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#c4a77d] animate-pulse"></span>
        )}
        {routeCatId === cat.id && !isSelected && (
          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#0f172a]"></span>
        )}
      </button>
    );
  };

  // ── Panel: Modul-Zeile ──
  const renderPanelItem = (item: SidebarItem) => {
    const Icon = item.icon;
    if (item.comingSoon) {
      return (
        <div
          key={item.id}
          title={`${item.label} — bald verfügbar`}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[15px] text-gray-300 cursor-not-allowed select-none"
        >
          <Icon size={18} strokeWidth={1.5} />
          <span className="text-[12.5px] font-bold tracking-tight flex-1 text-left">{item.label}</span>
          <span className="text-[7px] font-black tracking-[0.16em] uppercase text-[#c4a77d] bg-[#c4a77d]/10 px-1.5 py-0.5 rounded-md">Bald</span>
        </div>
      );
    }
    const isActive = currentId === item.id;
    return (
      <button
        key={item.id}
        onClick={() => {
          // Klick auf "E-Mails" (auch aus der Verwaltung-Liste) reaktiviert die E-Mail-Sub-Nav —
          // auch wenn die Route sich nicht ändert (man ist schon im E-Mail-Bereich).
          if (item.id === 'emails') setEmailNavDismissed(false);
          onNavigate(item.path);
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[15px] transition-all duration-300 group relative ${
          isActive
            ? 'bg-[#0f172a] text-white shadow-[0_14px_26px_-14px_rgba(15,23,42,0.5)]'
            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        <div className="relative">
          <Icon size={18} strokeWidth={isActive ? 2.3 : 1.6} />
          {renderBadges(item.id, 'panel')}
        </div>
        <span className="text-[12.5px] font-bold tracking-tight flex-1 text-left">{item.label}</span>
      </button>
    );
  };

  // Kontextuelle E-Mail-Sub-Nav (Postfach + Ordner + Neue E-Mail) statt der Kategorie-Items.
  const renderEmailNav = () => {
    if (!emailNav) return null;
    const ordner: { key: EmailOrdner; label: string; icon: React.ReactNode; badge?: number }[] = [
      { key: 'posteingang', label: 'Posteingang', icon: <Inbox size={17} strokeWidth={1.7} />, badge: emailNav.unreadCount || undefined },
      { key: 'offen', label: 'Offen', icon: <CircleDot size={17} strokeWidth={1.7} />, badge: emailNav.offenCount || undefined },
      { key: 'erledigt', label: 'Erledigt', icon: <CheckCircle2 size={17} strokeWidth={1.7} /> },
      { key: 'wiedervorlage', label: 'Wiedervorlage', icon: <Clock size={17} strokeWidth={1.7} />, badge: emailNav.wiedervorlageCount || undefined },
      { key: 'rueckmeldung', label: 'Rückmeldung', icon: <Hourglass size={17} strokeWidth={1.7} />, badge: emailNav.rueckmeldungCount || undefined },
      { key: 'gesendet', label: 'Gesendet', icon: <Send size={17} strokeWidth={1.7} /> },
      { key: 'geplant', label: 'Geplant', icon: <CalendarClock size={17} strokeWidth={1.7} />, badge: emailNav.geplantCount || undefined },
      { key: 'entwuerfe', label: 'Entwürfe', icon: <FilePen size={17} strokeWidth={1.7} />, badge: emailNav.entwuerfeCount || undefined },
      { key: 'werbung', label: 'Werbung', icon: <Megaphone size={17} strokeWidth={1.7} />, badge: emailNav.werbungCount || undefined },
      { key: 'archiviert', label: 'Archiviert', icon: <Archive size={17} strokeWidth={1.7} /> },
      { key: 'geloescht', label: 'Gelöscht', icon: <Trash2 size={17} strokeWidth={1.7} /> },
      { key: 'vorlagen', label: 'Vorlagen', icon: <FileText size={17} strokeWidth={1.7} /> },
      { key: 'einstellungen', label: 'Einstellungen', icon: <Settings2 size={17} strokeWidth={1.7} /> },
    ];
    return (
      <div className="flex flex-col gap-3">
        {/* Postfach */}
        <div>
          <p className="pl-2 pb-1.5 text-[9px] font-black uppercase tracking-widest text-gray-400">Postfach</p>
          {emailNav.konten.length <= 1 ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 text-[11px] font-bold text-gray-700">
              <Mail size={13} className="text-[#c4a77d] shrink-0" />
              <span className="truncate">{emailNav.konten[0]?.anzeigename || emailNav.konten[0]?.email_adresse || '—'}</span>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {emailNav.konten.map((k) => (
                <button
                  key={k.id}
                  onClick={() => emailNav.onSelectKonto(k.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-[11px] font-bold transition-colors ${k.id === emailNav.selectedKontoId ? 'bg-[#0f172a] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <Mail size={13} className="shrink-0" />
                  <span className="truncate">{k.anzeigename || k.email_adresse}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Neue E-Mail */}
        <button
          onClick={() => emailNav.onCompose()}
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-[#1a1d24] text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 transition-opacity active:scale-95"
        >
          <Plus size={14} /> Neue E-Mail
        </button>

        {/* Ordner */}
        <div className="flex flex-col gap-0.5">
          <p className="pl-3 pb-1 text-[9px] font-black uppercase tracking-widest text-gray-400">Ordner</p>
          {ordner.map((o) => {
            const aktiv = emailNav.activeTab === o.key;
            return (
              <button
                key={o.key}
                onClick={() => emailNav.onSelectTab(o.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[15px] transition-all ${aktiv ? 'bg-[#0f172a] text-white shadow-[0_14px_26px_-14px_rgba(15,23,42,0.5)]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
              >
                <span className={aktiv ? 'text-white' : 'text-gray-400'}>{o.icon}</span>
                <span className="text-[12.5px] font-bold tracking-tight flex-1 text-left">{o.label}</span>
                {o.badge ? (
                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${aktiv ? 'bg-white/20 text-white' : 'bg-[#c4a77d] text-white'}`}>{o.badge}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Objekte */}
        {emailNav.objekte.length > 0 && (
          <div className="flex flex-col gap-0.5 min-h-0">
            <p className="pl-3 pb-1 text-[9px] font-black uppercase tracking-widest text-gray-400">Objekte</p>
            <div
              ref={objektScrollRef}
              onDragOver={objektDragScroll}
              onDragLeave={(e) => { if (!objektScrollRef.current?.contains(e.relatedTarget as Node)) stoppeAutoScroll(); }}
              onDrop={stoppeAutoScroll}
              className="flex flex-col gap-0.5 max-h-64 overflow-y-auto custom-scrollbar pr-0.5"
            >
              {emailNav.objekte.map((o) => {
                const aktiv = emailNav.activeTab === 'objekt' && emailNav.selectedObjektId === o.id;
                const label = [o.objektnummer, o.bezeichnung].filter(Boolean).join(' · ') || 'Objekt';
                return (
                  <button
                    key={o.id}
                    onClick={() => emailNav.onSelectObjekt(o.id)}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverObjektId(o.id); }}
                    onDragLeave={() => setDragOverObjektId((cur) => (cur === o.id ? null : cur))}
                    onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('application/email-id'); setDragOverObjektId(null); if (id) emailNav.onAssignObjekt(id, o.id); }}
                    title={label}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-[13px] transition-all ${dragOverObjektId === o.id ? 'ring-2 ring-[#c4a77d] bg-[#c4a77d]/15 text-gray-900' : aktiv ? 'bg-[#0f172a] text-white shadow-[0_14px_26px_-14px_rgba(15,23,42,0.5)]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                  >
                    <span className={`shrink-0 ${aktiv ? 'text-white' : 'text-gray-400'}`}><Folder size={15} strokeWidth={1.7} /></span>
                    <span className="text-[12px] font-bold tracking-tight flex-1 text-left truncate">{label}</span>
                    {o.unread ? (
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0 ${aktiv ? 'bg-white/20 text-white' : 'bg-[#c4a77d] text-white'}`}>{o.unread}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`fixed lg:sticky top-0 left-0 z-[110] bg-white border-r border-gray-100 flex h-screen overflow-hidden
        transition-all duration-500 ease-in-out
        pt-[max(2rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]
        kurz:pt-[max(0.5rem,env(safe-area-inset-top))] kurz:pb-[max(0.5rem,env(safe-area-inset-bottom))]
        pl-[env(safe-area-inset-left)]
        ${collapsed ? 'w-[calc(286px+env(safe-area-inset-left))] lg:w-[74px]' : 'w-[calc(286px+env(safe-area-inset-left))]'}
        ${isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}`}
    >
      {/* ── RAIL ── */}
      <div className="w-[74px] shrink-0 flex flex-col items-center">
        <button
          onClick={toggle}
          onMouseEnter={(e) => showRailTip(e, collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen')}
          onMouseLeave={hideRailTip}
          aria-label="Sidebar ein-/ausklappen"
          className="border-[1.5px] border-black p-2 rounded-xl flex items-center justify-center bg-white shadow-sm hover:border-[#c4a77d] transition-all mb-5 mt-1"
        >
          <Home size={18} strokeWidth={1.5} className="text-black" />
        </button>

        {visibleTop.map(renderRailTop)}

        <div className="w-6 h-px bg-gray-100 my-2.5"></div>

        <nav className="flex flex-col items-center gap-1.5 overflow-y-auto no-scrollbar w-full px-2">
          {loading && !isSuperAdmin ? (
            <Loader2 className="animate-spin text-gray-200 mt-6" size={20} />
          ) : (
            visibleCats.map(renderRailCategory)
          )}
        </nav>

        <div className="flex-1"></div>

        {isDemoMode && (
          <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-2" title="Demo-Modus aktiv">
            <span className="text-[6px] font-black text-amber-600 uppercase">Demo</span>
          </div>
        )}

        <button
          onClick={() => onNavigate('/admin/profile')}
          onMouseEnter={(e) => showRailTip(e, fullName || userEmail || 'Profil')}
          onMouseLeave={hideRailTip}
          aria-label={fullName || userEmail || 'Profil'}
          className={`w-9 h-9 rounded-full bg-[#0f172a] flex items-center justify-center text-white text-[10px] font-black tracking-tighter shadow-md hover:bg-[#c4a77d] transition-colors overflow-hidden mb-2.5 ${
            currentId === 'profile' ? 'ring-2 ring-[#c4a77d]/40' : ''
          }`}
        >
          {avatarUrl ? <img src={avatarUrl} alt="Profil" className="w-full h-full object-cover" /> : initial}
        </button>

        <button
          onClick={onLogout}
          onMouseEnter={(e) => showRailTip(e, 'Abmelden')}
          onMouseLeave={hideRailTip}
          aria-label="Abmelden"
          className="w-10 h-10 rounded-xl bg-red-50/60 text-red-400 flex items-center justify-center hover:text-red-500 transition-all"
        >
          <LogOut size={16} strokeWidth={2.3} />
        </button>
      </div>

      {/* ── PANEL ── */}
      <div className="flex flex-col w-[212px] shrink-0 border-l border-gray-100">
        <div className="flex items-center justify-between px-5 pt-1.5 pb-4">
          <span className="text-[11px] font-black tracking-[0.18em] uppercase text-[#0f172a] truncate">
            {emailMode ? 'E-Mails' : (panelCategory?.label ?? '')}
          </span>
          <button
            onClick={toggle}
            title="Panel einklappen"
            className="hidden lg:flex w-6 h-6 rounded-lg items-center justify-center text-gray-300 hover:text-[#c4a77d] hover:bg-gray-50 transition-all shrink-0"
          >
            <ChevronsLeft size={15} />
          </button>
          <button onClick={onClose} className="lg:hidden p-1 text-gray-400 hover:text-black shrink-0">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto custom-scrollbar">
          {emailMode ? (
            renderEmailNav()
          ) : loading && !isSuperAdmin ? (
            <div className="py-10 flex flex-col items-center justify-center gap-3">
              <Loader2 className="animate-spin text-gray-200" size={20} />
              <span className="text-[8px] font-black text-gray-300 uppercase tracking-widest">Lade Module...</span>
            </div>
          ) : panelCategory ? (
            panelCategory.items.map(renderPanelItem)
          ) : (
            <div className="py-10 text-center text-[9px] font-black text-gray-300 uppercase tracking-widest">Keine Module</div>
          )}
        </nav>

        <div className="px-3 pt-3 kurz:pt-1.5">
          <div
            onClick={() => onNavigate('/admin/profile')}
            className={`bg-white p-3 kurz:p-2 rounded-[20px] flex items-center gap-3 border border-gray-100 shadow-[0_12px_30px_-16px_rgba(0,0,0,0.1)] cursor-pointer hover:border-[#c4a77d]/30 transition-all group ${
              currentId === 'profile' ? 'border-[#c4a77d]/30 ring-1 ring-[#c4a77d]/10' : ''
            }`}
          >
            <div className="w-9 h-9 rounded-full bg-[#0f172a] flex items-center justify-center text-white text-[10px] font-black tracking-tighter shadow-md group-hover:bg-[#c4a77d] transition-colors overflow-hidden shrink-0">
              {avatarUrl ? <img src={avatarUrl} alt="Profil" className="w-full h-full object-cover" /> : initial}
            </div>
            <div className="flex flex-col overflow-hidden">
              <span className="text-[11px] font-bold text-[#0f172a] truncate leading-tight">{fullName || userEmail}</span>
              <span className="text-[7.5px] font-black tracking-[0.16em] text-[#c4a77d] uppercase mt-0.5 kurz:hidden">{getRoleLabel()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hover-Tooltip für Rail-Icons — per Portal an document.body, sonst schneidet das
          overflow-hidden der aside es ab (die aside ist durch translate-x ein transform-
          Containing-Block, daher wirkt position:fixed relativ zur aside, nicht zum Viewport). */}
      {railTip && createPortal(
        <div
          role="tooltip"
          className="fixed z-[200] pointer-events-none -translate-y-1/2 px-2.5 py-1.5 rounded-lg bg-[#0f172a] text-white text-[10px] font-bold tracking-tight whitespace-nowrap shadow-[0_8px_20px_-6px_rgba(15,23,42,0.45)]"
          style={{ top: railTip.top, left: railTip.left }}
        >
          {railTip.label}
        </div>,
        document.body,
      )}
    </aside>
  );
};

export default Sidebar;
